/**
 * Serial per-itinerary mutation queue with optimistic updates (plan D8 / spec
 * "Editor auto-save").
 *
 * Semantics:
 * - One in-flight itinerary mutation at a time; subsequent edits queue in order
 *   so edit #4 is never built on optimistic state the server rejected at #3.
 * - Each enqueued op carries an `apply` (optimistic local transform) so the UI
 *   updates instantly, and a `run` that calls the real tRPC mutation.
 * - On success: server truth replaces the optimistic snapshot; pill -> Saved.
 * - On failure: the failed op's optimistic change is rolled back to the
 *   pre-queue server snapshot + replay of the remaining-confirmed ops, the
 *   queue PAUSES (held, not discarded). `retry()` replays from the failed op.
 *   `dismiss()` drops the failed op + every queued op whose `dependsOn`
 *   (dayId/stopId) matches the failed op's, then resyncs from server truth.
 * - Offline (navigator.onLine === false at failure time): pill -> Offline,
 *   queue held; a window `online` event drains it.
 *
 * This hook is itinerary-local (one instance per editor mount). It owns the
 * optimistic `SerializedItinerary` snapshot the editor renders.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SerializedItinerary } from './itineraryTypes';

export type AutoSaveStatus = 'saved' | 'saving' | 'retrying' | 'offline';

/** What a queued op structurally touches — used for the Dismiss dependent set. */
export interface OpScope {
  dayId?: string;
  stopId?: string;
}

export interface QueuedOp {
  /** Stable id for keys / dedupe. */
  id: string;
  /** Human label for the error toast ("move stop", "add day", …). */
  label: string;
  /** Optimistic local transform applied immediately. */
  apply: (current: SerializedItinerary) => SerializedItinerary;
  /** The real server call; resolves to fresh server truth. */
  run: () => Promise<SerializedItinerary>;
  /** Day/stop this op depends on (Dismiss drops same-day/same-stop queued ops). */
  dependsOn: OpScope;
}

export interface MutationQueueState {
  /** The itinerary the editor should render (optimistic-ahead of server). */
  itinerary: SerializedItinerary;
  status: AutoSaveStatus;
  /** Set when the queue is paused on a failed op. */
  failure: { opId: string; label: string; message: string } | null;
  enqueue: (op: QueuedOp) => void;
  retry: () => void;
  dismiss: () => void;
  /** Replace the snapshot from a fresh server query (e.g. external refetch). */
  resync: (next: SerializedItinerary) => void;
}

const isSameDependent = (a: OpScope, b: OpScope): boolean =>
  (a.stopId !== undefined && a.stopId === b.stopId) ||
  (a.dayId !== undefined && a.dayId === b.dayId);

interface UseMutationQueueArgs {
  /** First server-truth snapshot (from getBySlug). */
  initial: SerializedItinerary;
  /** Pull fresh server truth (Dismiss / hard resync). Should be a stable ref. */
  refetch: () => Promise<SerializedItinerary | undefined>;
}

export const useItineraryMutationQueue = ({
  initial,
  refetch,
}: UseMutationQueueArgs): MutationQueueState => {
  // Last confirmed server truth — the rollback floor.
  const serverSnapshot = useRef<SerializedItinerary>(initial);
  const [itinerary, setItinerary] = useState<SerializedItinerary>(initial);
  const [status, setStatus] = useState<AutoSaveStatus>('saved');
  const [failure, setFailure] = useState<MutationQueueState['failure']>(null);

  const queue = useRef<QueuedOp[]>([]);
  const draining = useRef(false);
  const paused = useRef(false);

  // Recompute the optimistic view = server snapshot + every still-queued op.
  const rebuildOptimistic = useCallback(() => {
    let next = serverSnapshot.current;
    for (const op of queue.current) {
      next = op.apply(next);
    }
    setItinerary(next);
  }, []);

  const drain = useCallback(async () => {
    if (draining.current || paused.current) return;
    if (queue.current.length === 0) {
      setStatus('saved');
      return;
    }
    draining.current = true;
    setStatus('saving');
    try {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- paused.current is mutated inside the loop body (ref); the narrowing is not statically provable.
      while (queue.current.length > 0 && !paused.current) {
        const op = queue.current[0];
        try {
          const fresh = await op.run();
          // Success: this op is now server truth; drop it, advance snapshot.
          serverSnapshot.current = fresh;
          queue.current.shift();
          rebuildOptimistic();
        } catch (err) {
          // Failure: pause. Roll back to snapshot + remaining-confirmed
          // (none are confirmed past head, so just snapshot + queue replay
          //  minus this op is shown after the user chooses Retry/Dismiss).
          paused.current = true;
          const offline =
            typeof navigator !== 'undefined' && navigator.onLine === false;
          setFailure({
            opId: op.id,
            label: op.label,
            message:
              err instanceof Error
                ? err.message
                : 'Could not save that change',
          });
          setStatus(offline ? 'offline' : 'retrying');
          // Roll the optimistic view back to pre-failure: snapshot replayed
          // through the queue EXCLUDING the failed head op so the rejected
          // change visibly reverts (reduced-motion aware revert is the
          // consumer's concern — state flips instantly here).
          let view = serverSnapshot.current;
          for (const q of queue.current.slice(1)) view = q.apply(view);
          setItinerary(view);
          return;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- paused.current may have been set true by the catch above; ref mutation is not statically tracked.
      if (queue.current.length === 0 && !paused.current) {
        setStatus('saved');
      }
    } finally {
      draining.current = false;
    }
  }, [rebuildOptimistic]);

  const enqueue = useCallback(
    (op: QueuedOp) => {
      queue.current.push(op);
      rebuildOptimistic();
      if (!paused.current) void drain();
    },
    [drain, rebuildOptimistic]
  );

  const retry = useCallback(() => {
    if (!paused.current) return;
    paused.current = false;
    setFailure(null);
    setStatus('saving');
    void drain();
  }, [drain]);

  const dismiss = useCallback(() => {
    const head: QueuedOp | undefined = queue.current.at(0);
    if (head) {
      const failedScope = head.dependsOn;
      queue.current = queue.current.filter(
        (q) => q !== head && !isSameDependent(q.dependsOn, failedScope)
      );
    }
    paused.current = false;
    setFailure(null);
    // Resync from server truth, then replay any surviving (independent) ops.
    setStatus('saving');
    void refetch().then((fresh) => {
      if (fresh) {
        serverSnapshot.current = fresh;
      }
      rebuildOptimistic();
      void drain();
    });
  }, [drain, refetch, rebuildOptimistic]);

  const resync = useCallback(
    (next: SerializedItinerary) => {
      serverSnapshot.current = next;
      if (queue.current.length === 0) {
        setItinerary(next);
        setStatus('saved');
      } else {
        rebuildOptimistic();
      }
    },
    [rebuildOptimistic]
  );

  // Offline -> online: drain the held queue (spec auto-save Offline path).
  useEffect(() => {
    const onOnline = () => {
      if (paused.current && status === 'offline') {
        paused.current = false;
        setFailure(null);
        setStatus('saving');
        void drain();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [drain, status]);

  return useMemo(
    () => ({
      itinerary,
      status,
      failure,
      enqueue,
      retry,
      dismiss,
      resync,
    }),
    [itinerary, status, failure, enqueue, retry, dismiss, resync]
  );
};
